/**
 * Textos de display de la plantilla de carga masiva.
 *
 * Cada uno de estos strings se escribe DOS veces en el mismo archivo: una en la hoja
 * oculta `Listas` (que alimenta el desplegable de la columna) y otra en la celda de datos
 * cuando la plantilla sale prellenada (descarga de guías con error). Si las dos versiones
 * no coinciden CARÁCTER POR CARÁCTER, Excel marca la celda prellenada como valor fuera de
 * lista y el operador ve en rojo una fila que en realidad estaba bien.
 *
 * Por eso viven acá y no como closures dentro de `exportFormatoShipmentXLSX_V2WithCoords`:
 * el generador de la plantilla y el mapper `ShipmentGroup → fila` (bulkTemplateRows.ts)
 * tienen que leer la misma definición.
 */

import { serviceLabel } from './authorizedServices'

/**
 * Etiqueta de `DeliveryEnum.OFFICE` en carga masiva.
 *
 * El código interno sigue siendo '10'; esto es solo el texto que ve el operador en el
 * desplegable y el literal contra el que matchean todas las fórmulas SEARCH()/IF() de
 * "¿es tienda?" de la plantilla.
 */
export const TIENDA_LABEL = 'Tienda'

/** Etiqueta de `DeliveryEnum.DELIVERY`. Mismo criterio que `TIENDA_LABEL`. */
export const DOMICILIO_LABEL = 'Domicilio'

/**
 * Prefija con comilla simple los valores que Excel interpretaría como fórmula
 * (inyección de fórmulas CSV/Excel — OWASP). Los datos vienen del backend.
 *
 * OJO: aplicar SOLO a columnas de texto. Una latitud negativa ("-66.9147") empieza con
 * '-' y saldría como "'-66.9147"; ExcelJS escribe esa comilla como carácter real (no como
 * el marcador de texto que Excel agrega al tipear), así que al re-subir el archivo
 * `extractCoord` leería basura.
 */
export const sanitizeForExcel = (v: string): string => {
  if (!v) return ''
  const first = v.charAt(0)
  if (
    first === '=' ||
    first === '+' ||
    first === '-' ||
    first === '@' ||
    first === '\t' ||
    first === '\r'
  ) {
    return "'" + v
  }
  return v
}

/** Forma mínima de una ciudad del catálogo (bulkCatalogs.cities). */
export interface CityLike {
  locationCode: string
  locationName: string
}

/** Forma mínima de una tienda/oficina del catálogo. */
export interface BusinessUnitLike {
  buCode: string
  buName?: string
  buArea?: string
  location?: { name?: string; address?: string; locationCode?: string }
}

/**
 * Forma mínima de un servicio autorizado.
 *
 * El texto del servicio NO se define acá: ya existe `serviceLabel` en
 * `utils/authorizedServices.ts`, que además es el que usa `resolveServiceIdFromCell` para
 * el camino de vuelta. Definir otro sería romper ese round-trip por duplicación.
 */
export interface ServiceOptionLike {
  serviceID: number
  serviceShortName?: string
  serviceDescription?: string
}

/**
 * Ciudad como "Localidad - Código" (dropdown de Ciudad, autofill por tienda y por
 * favorito). El parser del import extrae el código del sufijo tras el último " - ".
 * Sin match en el catálogo se devuelve el código crudo, para que la celda quede con algo
 * que el operador pueda corregir en vez de vacía.
 */
export const cityDisplay = (
  citiesType: CityLike[],
  locationCode?: string
): string => {
  if (!locationCode) return ''
  const c = citiesType.find((ct) => ct.locationCode === locationCode)
  return c ? `${c.locationName} - ${c.locationCode}` : locationCode
}

/**
 * Tienda destino como "buCode - buName" (columna J de Listas). El parser resuelve por el
 * buCode del prefijo.
 */
export const storeDisplay = (bu: BusinessUnitLike): string =>
  `${bu.buCode} - ${bu.buName || bu.buCode}`

/** Igual que `storeDisplay` pero desde un buCode suelto + el catálogo. */
export const storeDisplayByCode = (
  businessUnits: BusinessUnitLike[],
  buCode?: string
): string => {
  if (!buCode) return ''
  const bu = businessUnits.find((b) => String(b.buCode) === String(buCode))
  return bu ? storeDisplay(bu) : buCode
}

/**
 * Oficina ORIGEN como "buCode - nombre" (columna R de Listas). Difiere de `storeDisplay`
 * en la preferencia del nombre: acá manda `location.name`, que es como se listan las
 * oficinas origen del contrato.
 */
export const originOfficeDisplay = (bu: BusinessUnitLike): string =>
  `${bu.buCode} - ${bu.location?.name || bu.buName || bu.buCode}`

/** Igual que `originOfficeDisplay` pero desde un buCode suelto + el catálogo. */
export const originOfficeDisplayByCode = (
  originBusinessUnits: BusinessUnitLike[],
  buCode?: string
): string => {
  if (!buCode) return ''
  const bu = originBusinessUnits.find(
    (b) => String(b.buCode) === String(buCode)
  )
  return bu ? originOfficeDisplay(bu) : buCode
}

/**
 * Servicio (columna T de Listas) desde un serviceID suelto + la lista autorizada.
 * El texto lo pone `serviceLabel`, no este módulo: es la MISMA función que usa
 * `resolveServiceIdFromCell` para leer la celda de vuelta.
 */
export const serviceDisplayById = (
  serviceOptions: ServiceOptionLike[],
  serviceId?: number
): string => {
  if (serviceId == null) return ''
  const s = serviceOptions.find((o) => Number(o.serviceID) === Number(serviceId))
  return s ? serviceLabel(s) : ''
}
